import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { AuthImg } from '../../components/portal/AuthImg';
import { Plus, Loader2, NotebookPen, Trash2, Pencil } from 'lucide-react';
import { jobCode, isConstruction } from '../../utils/jobCode';

/** One note card with thumbnails; used here and on the admin project page. */
export function SiteNoteCard({ note, onDelete, onEdit, onOpenPhoto, showProject = true, light = false }) {
  const { features } = useAuth();
  const shell = light ? 'bg-white border-gray-200' : 'bg-[#2A3240] border-white/10';
  const text = light ? 'text-gray-800' : 'text-white';
  const muted = light ? 'text-gray-500' : 'text-gray-400';
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${shell}`} data-testid={`site-note-${note.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {showProject && <div className="flex items-center gap-2"><span className={`font-bold text-sm ${isConstruction(note) ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(note, features)}</span><span className={`text-xs truncate ${muted}`}>{note.project_address}</span></div>}
          <div className={`text-[11px] ${muted}`}>{note.user_name} · {format(new Date(note.created_at), 'EEE d MMM, HH:mm')}{note.edited_at ? ' · edited' : ''}</div>
        </div>
        {onEdit && <button type="button" onClick={() => onEdit(note)} className={`w-9 h-9 rounded-lg flex items-center justify-center ${muted} hover:text-white`} data-testid={`site-note-edit-${note.id}`}><Pencil className="w-4 h-4" /></button>}
        {onDelete && <button type="button" onClick={() => onDelete(note)} className={`w-9 h-9 rounded-lg flex items-center justify-center ${muted} hover:text-red-400`} data-testid={`site-note-delete-${note.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
      {note.note && <p className={`text-sm whitespace-pre-wrap ${text}`} data-testid={`site-note-text-${note.id}`}>{note.note}</p>}
      {note.photo_count > 0 && (
        <div className="grid grid-cols-4 gap-1.5" data-testid={`site-note-photos-${note.id}`}>
          {note.photos.map(p => (
            <AuthImg key={p.idx} src={`/portal/site-notes/${note.id}/photos/${p.idx}?thumb=true`} alt="" className="aspect-square w-full object-cover rounded-lg cursor-zoom-in bg-black/20" onClick={() => onOpenPhoto?.(note, p.idx)} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PhotoLightbox({ open, onClose, src, title }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-black border-white/10 text-white p-0 w-[96vw] max-w-[430px] h-[85vh] flex flex-col overflow-hidden" data-testid="photo-lightbox">
        <div className="px-4 py-2 border-b border-white/10 pr-12"><DialogTitle className="text-sm truncate">{title}</DialogTitle></div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-black">{src && <AuthImg src={src} alt="" className="max-w-full max-h-full object-contain" />}</div>
      </DialogContent>
    </Dialog>
  );
}

export function PortalSiteNotes() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/portal/site-notes'); setItems(r.data || []); }
    catch { toast.error('Could not load your notes'); setItems([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (note) => {
    if (!window.confirm('Remove this note and its photos?')) return;
    try { await api.delete(`/portal/site-notes/${note.id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="site-notes-page">
      <Button onClick={() => navigate('/portal/site-notes/new')} className="w-full h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold text-base" data-testid="new-site-note-btn"><Plus className="w-5 h-5 mr-2" /> Add note & photos</Button>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : items.length === 0 ? (
        <div className="py-12 text-center text-gray-500" data-testid="site-notes-empty"><NotebookPen className="w-10 h-10 mx-auto mb-2 opacity-40" />No site notes yet. Use the button above or tap a job on your Schedule.</div>
      ) : (
        <div className="space-y-2" data-testid="site-notes-list">
          {items.map(n => <SiteNoteCard key={n.id} note={n} onDelete={remove} onEdit={(note) => navigate(`/portal/site-notes/${note.id}/edit`)} onOpenPhoto={(note, idx) => setLightbox({ src: `/portal/site-notes/${note.id}/photos/${idx}`, title: `${note.project_code} · ${format(new Date(note.created_at), 'd MMM HH:mm')}` })} />)}
        </div>
      )}
      <PhotoLightbox open={!!lightbox} onClose={() => setLightbox(null)} src={lightbox?.src} title={lightbox?.title} />
    </div>
  );
}
